import { useEffect, useState } from "react";
import { CalendarDays, Check, Package } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fecha } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Actividades() {
  const { activeId } = useAuth();
  const [actividades, setActividades] = useState(null);
  const [combos, setCombos] = useState(null);
  const [inscritos, setInscritos] = useState([]);
  const [busy, setBusy] = useState(null);

  const cargar = () => {
    api.get("/actividades").then((r) => setActividades(r.data));
    api.get("/combos").then((r) => setCombos(r.data));
    if (activeId) api.get(`/estudiantes/${activeId}/combos`).then((r) => setInscritos(r.data));
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const inscribir = async (cid) => {
    setBusy(cid);
    try {
      const { data } = await api.post(`/estudiantes/${activeId}/combos/${cid}`);
      toast.success(`Inscripción realizada. Factura ${data.factura.ncf} generada por ${money(data.factura.monto_total)}`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(null);
  };

  if (!actividades || !combos) return <Loading />;

  const inscritosIds = inscritos.map((i) => i.combo_id);
  const porMes = actividades.reduce((acc, a) => {
    const k = new Date(a.fecha_evento).toLocaleDateString("es-DO", { month: "long", year: "numeric" });
    acc[k] = acc[k] || [];
    acc[k].push(a);
    return acc;
  }, {});

  return (
    <div data-testid="activities-page">
      <PageTitle title="Actividades y combos"
                 subtitle="Calendario institucional y catálogo de paquetes complementarios para inscribir al estudiante." />

      <Tabs defaultValue="calendario">
        <TabsList>
          <TabsTrigger value="calendario" data-testid="tab-calendario">Calendario</TabsTrigger>
          <TabsTrigger value="combos" data-testid="tab-combos">Combos complementarios</TabsTrigger>
        </TabsList>

        <TabsContent value="calendario" className="mt-8 space-y-8" data-testid="calendario-actividades">
          {Object.entries(porMes).map(([mes, items]) => (
            <div key={mes}>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h3 className="font-display text-lg font-semibold capitalize">{mes}</h3>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((a) => (
                  <div key={a.id} className="lift rounded-lg border border-border/60 bg-card p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-display text-base font-semibold">{a.nombre}</h4>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{a.categoria}</Badge>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{a.descripcion}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-mono text-xs text-muted-foreground">{fecha(a.fecha_evento)}</span>
                      <span className="font-mono text-sm font-semibold text-primary">{money(a.costo)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="combos" className="mt-8">
          <div className="grid gap-8 lg:grid-cols-3" data-testid="catalogo-combos">
            {combos.map((c) => {
              const ya = inscritosIds.includes(c.id);
              return (
                <article key={c.id} className="lift overflow-hidden rounded-lg border border-border/60 bg-card">
                  {c.imagen_url && (
                    <img src={c.imagen_url} alt={c.nombre} className="h-40 w-full object-cover" />
                  )}
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-2 text-primary">
                      <Package className="h-4 w-4" />
                      <h3 className="font-display text-lg font-bold">{c.nombre}</h3>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{c.descripcion}</p>
                    <ul className="mt-5 space-y-2">
                      {c.actividades.map((a) => (
                        <li key={a.id} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-primary" /> {a.nombre}
                          </span>
                          <span className="font-mono text-xs text-muted-foreground">{money(a.costo)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-6 font-display text-2xl font-extrabold tracking-tight">{money(c.precio_total)}</p>
                    <Button className="mt-5 w-full rounded-full" disabled={ya || busy === c.id}
                            onClick={() => inscribir(c.id)} data-testid={`inscribir-combo-${c.id}`}>
                      {ya ? "Ya inscrito" : busy === c.id ? "Procesando…" : "Inscribir estudiante"}
                    </Button>
                  </div>
                </article>
              );
            })}
            {combos.length === 0 && <Empty>No hay combos disponibles.</Empty>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
